import Highcharts from "highcharts";
import { BORDER_COLORS, FONT_COLORS } from "./colors";

type TChartSeries = {
  name: SeriesNames;
  color: string;
  prefix: string | null;
  suffix: string | null;
  decimals?: number;
};

export enum SeriesNames {
  Amazon = "Amazon",
  FBA = "FBA",
  FBM = "FBM",
  BuyBox = "Buy Box",
  New = "New",
  SalesRank = "Sales Rank",
  MonthlySold = "Monthly Sold",
  OffersCount = "Offers Count",
  Rating = "Rating",
  ReviewCount = "Review Count",
}

export const CHART_LABEL_FONT_SIZE = "9px";

export const CHART_SERIES: Record<SeriesNames, TChartSeries> = {
  [SeriesNames.Amazon]: {
    name: SeriesNames.Amazon,
    color: "#FFA500",
    prefix: "$",
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.FBA]: {
    name: SeriesNames.FBA,
    color: "#ff5722",
    prefix: "$",
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.FBM]: {
    name: SeriesNames.FBM,
    color: "#039be5",
    prefix: "$",
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.BuyBox]: {
    name: SeriesNames.BuyBox,
    color: "#ff00b4",
    prefix: "$",
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.New]: {
    name: SeriesNames.New,
    color: "#8888DD",
    prefix: "$",
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.SalesRank]: {
    name: SeriesNames.SalesRank,
    color: "#8FBC8F",
    prefix: null,
    suffix: null,
    decimals: 2,
  },
  [SeriesNames.MonthlySold]: {
    name: SeriesNames.MonthlySold,
    color: "#FFA500",
    prefix: null,
    suffix: "+",
  },
  [SeriesNames.OffersCount]: {
    name: SeriesNames.OffersCount,
    color: "#8888DD",
    prefix: null,
    suffix: null,
  },
  [SeriesNames.Rating]: {
    name: SeriesNames.Rating,
    color: "#009688",
    prefix: "★",
    suffix: null,
  },
  [SeriesNames.ReviewCount]: {
    name: SeriesNames.ReviewCount,
    color: "#8AB300",
    prefix: null,
    suffix: null,
  },
};

export const CHARTS_SHARED_OPTIONS: Highcharts.Options = {
  accessibility: {
    enabled: false,
  },
  title: {
    text: "",
  },
  legend: {
    itemStyle: {
      fontSize: CHART_LABEL_FONT_SIZE,
    },
    verticalAlign: "top",
    borderWidth: 1,
    borderRadius: 4,
    margin: 16,
    borderColor: BORDER_COLORS.default,
  },
  chart: {
    animation: false,
    spacingLeft: 1,
    spacingRight: 1,
    marginLeft: 30,
    marginRight: 30,
    plotBorderColor: BORDER_COLORS.default,
    plotBorderWidth: 1,
    style: {
      padding: "0",
    },
    spacing: [4, 0, 4, 0],
  },
  yAxis: {
    title: {
      text: "",
    },
    labels: {
      x: -4,
      style: {
        fontSize: CHART_LABEL_FONT_SIZE,
        color: FONT_COLORS.label,
      },
    },
  },
  xAxis: {
    type: "datetime",
    gridLineWidth: 1,
    lineColor: BORDER_COLORS.default,
    tickColor: BORDER_COLORS.default,
    maxPadding: 0,
    labels: {
      style: {
        fontSize: CHART_LABEL_FONT_SIZE,
        color: FONT_COLORS.label,
      },
    },
    crosshair: {
      snap: false,
      width: 1.5,
      dashStyle: "LongDash",
      zIndex: 3,
    },
  },
  tooltip: {
    shared: true,
    outside: false,
    useHTML: true,
    followPointer: true,
    hideDelay: 100,
    formatter: function () {
      if (!this.x) return false;

      const chart = this.series.chart;
      const x = +this.x;

      const TooltipLine = (s: Highcharts.Series, closest: Highcharts.Point) => {
        const prefix = CHART_SERIES[s.name as SeriesNames].prefix ?? "";
        const suffix = CHART_SERIES[s.name as SeriesNames].suffix ?? "";

        const isRating = s.name === SeriesNames.Rating;
        const decimals = isRating
          ? 1
          : CHART_SERIES[s.name as SeriesNames].decimals || 0;

        return `
          <div style="display: flex; justify-content: space-between; color:${s.color}; font-size: 10px">
            <p style="margin: 0">${s.name}:</p>
            <p style="margin: 0"><b>${prefix}${Highcharts.numberFormat(closest.y!, decimals, ".", ",")}${suffix}</b></p>
          </div>
        `;
      };

      const TooltipFooter = (timestamp: number) => `
        <p style="text-align: end; margin: 4px 0 0; font-size: 9px">
          ${Highcharts.dateFormat("%H:%M %a %b %e, %Y", timestamp)}
        </p>
      `;

      const TooltipWrapper = (content: string) => `
        <div style="display: flex; flex-direction: column; min-width: 100px">
          ${content}
        </div>
      `;

      const lines = chart.series
        .filter((s) => s.visible)
        .map((s) => {
          const closest = s.points.reduce(
            (acc: Highcharts.Point | null, pt) => {
              if (pt.y === null) return acc;
              if (!acc) return pt;
              return Math.abs(pt.x - x) < Math.abs(acc.x - x) ? pt : acc;
            },
            null,
          );

          if (closest && closest.y !== null) {
            return TooltipLine(s, closest);
          }
          return "";
        })
        .filter(Boolean);

      const footer = TooltipFooter(x);
      return TooltipWrapper(lines.join("") + footer);
    },
    positioner: function (labelWidth, labelHeight, point) {
      const directionX = point.plotX >= this.chart.plotWidth / 2 ? "l" : "r";
      const plotToChartDiff = this.chart.chartWidth - this.chart.plotWidth;
      const rightX = point.plotX + plotToChartDiff;
      const leftX = rightX - labelWidth;

      return {
        x: directionX === "r" ? rightX : leftX,
        y: this.chart.plotTop + this.chart.plotHeight - labelHeight,
      };
    },
  },
  plotOptions: {
    series: {
      lineWidth: 1,
      step: "left",
      marker: {
        enabled: false,
      },
      states: {
        hover: {
          lineWidth: 2,
        },
        inactive: {
          opacity: 0.5,
        },
      },
    },
  },
  credits: {
    enabled: false,
  },
};
